import useInitialConfigStore from "../../../storeModules/useInitialConfigStore";
import findStep from "./findStep";
import trackConversacaoStep from "../../../utils/trackConversacaoStep";
import useBotControlStore from "../../../storeModules/useBotControlStore";
import useUserStore from "../../../storeModules/useUserStore";
import useConsorcioStore from "../../../storeModules/useConsorcioStore";
import { sanitizeUrl } from "../../../utils/sanitize";
import {
  tagIdentificacao,
  tagDadosContrato,
  tagBtnWhatsapp,
  tagAlterarDadosContrato,
  tagDetalhesPlano,
} from "../../../utils/sendTag";
import {
  acionarEspecialistaTag,
  termsAcceptedTag,
  viewItemTag,
} from "../../../utils/sendTag/index";
import {
  getCep,
  getProposta,
  getCatalog,
  validateForbiddenWords,
  getPlanDetails,
} from "../../../api/requests";
import sha256 from "crypto-js/sha256";
import {
  ProdutosNomes,
  tipoPlanoNome,
  Catalog,
  ProdutosNomesCopy,
} from "../../../utils/constants";
import addCepMask from "../../../utils/addCepMask";
import GroupInfoOptions from "../../../components/group-info/group-info";
import { startNewConversacaoSession } from "../../../api/requestsFunctions/conversacao";
import dayjs from "dayjs";
import hasUrlCoupon from "../../../utils/hasUrlCoupon";
import useInsuranceStore from "../../../storeModules/useInsuranceStore";
import noConnectionHandler from "../../../utils/noConnectionHandler";
import getFees from "../../../utils/getFees";
import getCategoryName from "../../../utils/getCategoryName";
import getRendaQualificada from "../../../utils/getRendaQualificada";
import { getRemoteValueEnv } from "../../../setup";

const { isInsuranceHired } = useInsuranceStore.getState();

const isTextFlux = () => {
  const { isActiveToAllBots, isActiveToOneRoute } = JSON.parse(
    getRemoteValueEnv("isTestFlux_dev", "isTestFlux_prod").asString(),
  );

  if (isActiveToAllBots) {
    return true;
  }

  return window.location.pathname.startsWith(isActiveToOneRoute);
};

const handleShowPlanDetails = () => {
  tagDetalhesPlano();
  viewItemTag();
  useBotControlStore.setState({
    currentStep: findStep("confirma_detalhes_plano").index,
  });
};

const handleShowSelectPlanConfirm = () => {
  tagDetalhesPlano();
  viewItemTag();
  useBotControlStore.setState({
    currentStep: findStep("confirma_plano_selecionado").index,
  });
};

const isSimulateAgain = () => {
  return useUserStore.getState().isSimulateAgain;
};

const handleGetProposta = async () => {
  await noConnectionHandler().then(() => {
    getProposta();
  });
};

const handleGetCatalog = async () => {
  const props = {
    minValor: useUserStore.getState().basic.valorCredito,
    tipo: Catalog.generateBy.CREDITO,
    categorias: Catalog.category[useConsorcioStore.getState().categoriaId],
    empresaId: useInitialConfigStore.getState().empresaId,
    pagaMetade: false,
    pagaIntegral: false,
    pagaReduzida: false,
    sort: 0,
    renda: getRendaQualificada(useUserStore.getState().basic),
    cotarSeguro: useInitialConfigStore.getState().seguro,
  };

  await noConnectionHandler().then(() => {
    getCatalog(props);
  });
};

const CAROUSEL_RESUME_STEPS = new Set([
  "carrega_planos",
  "selecao_planos",
  "mensagem_carrossel_planos",
]);

export { CAROUSEL_RESUME_STEPS };

export const ensurePlansLoadedForResume = (resumeStepName, historico = []) => {
  if (!CAROUSEL_RESUME_STEPS.has(resumeStepName)) return false;

  const { fetchedPlans } = useConsorcioStore.getState();
  const hasPlansInStore =
    Array.isArray(fetchedPlans) && fetchedPlans.length > 1;

  const userAlreadySelectedPlan = historico.some(
    (entry) =>
      entry.sender === "user" && entry.step === "mensagem_carrossel_planos",
  );

  if (userAlreadySelectedPlan && hasPlansInStore) return false;

  useBotControlStore.setState({
    skipStepTracking: true,
    skipStepAction: false,
    resumePlansTargetStep: "mensagem_carrossel_planos",
    currentStep: findStep("carrega_planos").index,
  });

  return true;
};

export const handleGetCep = async (errorAt) => {
  useUserStore.setState({
    cepErrorAt: errorAt,
  });

  await noConnectionHandler().then(() => {
    getCep();
  });
};

export const shouldSelectCoupon = async () => {
  // COUPON FLAG
  const { shouldUseCoupon } = useInitialConfigStore.getState();

  if (!!hasUrlCoupon() && shouldUseCoupon) {
    useBotControlStore.setState({
      currentStep: findStep("cupom_url_detectado").index,
    });
  } else if (shouldUseCoupon) {
    useBotControlStore.setState({
      currentStep: findStep("pergunta_cupom").index,
    });
  } else {
    await noConnectionHandler().then(() => {
      handleGetProposta();
    });
  }
};

const proposalSteps = [
  {
    name: "carrega_planos",
    sender: "bot",
    text: () => {
      return isSimulateAgain() ? null : "Obrigado!";
    },
    isWaitingForFetch: true,
    action: () => {
      handleGetCatalog();
    },
  },
  {
    name: "selecao_planos",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const categoryName = getCategoryName()?.toLowerCase();
      const categoryNameMessage = categoryName ? `de ${categoryName}` : "";

      const userBasicName = useUserStore.getState().basic.nome;
      const planVerb = isTextFlux() ? "simulei" : "selecionei";
      return `${userBasicName}, ${planVerb} os <b>melhores planos ${categoryNameMessage}</b> disponíveis pra você.`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("mensagem_carrossel_planos").index,
      });
    },
  },
  {
    name: "mensagem_carrossel_planos",
    sender: "bot",
    isWaitingForFetch: true,
    text: () =>
      "Escolha o valor de parcela que mais se adapta ao seu orçamento para ver mais detalhes:",
    useCarouselPlan: true,
  },
  {
    name: "mensagem_desconto_parcela",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      const userTipoBem = useConsorcioStore.getState().tipoBem;
      const { empresaId } = useInitialConfigStore.getState();
      const bemCopy = ProdutosNomesCopy[userTipoBem];

      const isBlackFriday = empresaId === 97;

      return `<b>${userBasicName}!</b> Olha, para te incentivar ainda mais a conquistar ${bemCopy}, eu vou pagar <b>${
        isBlackFriday ? "60%" : "50%"
      } do valor da sua primeira parcela.</b>`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("valor_desconto_parcela").index,
      });
    },
  },
  {
    name: "valor_desconto_parcela",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const userValorPagamento =
        useConsorcioStore.getState().selectedPlano?.planoValorPagamento;

      return `O valor da sua primeira parcela será de apenas <b>${(
        userValorPagamento ?? 0
      ).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</b>`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("confirmacao_informacoes").index,
      });
    },
  },
  {
    name: "confirmacao_informacoes",
    sender: "bot",
    text: () => {
      const baseText =
        "Falta pouco para <b>iniciar a sua conquista</b>. Só preciso confirmar algumas informações";
      const complement = isTextFlux() ? " para gerar sua proposta. 😎" : ". 😎";
      return `${baseText}${complement}`;
    },
    options: () => [
      {
        text: "SEGUIR EM FRENTE",
        action: async () => {
          useBotControlStore.setState({
            currentStep: findStep("titular_plano").index,
          });
        },
      },
      ...(isTextFlux()
        ? [
            {
              text: "VER DETALHES",
              className: "tertiary",
              action: handleShowSelectPlanConfirm,
            },
          ]
        : []),
      {
        text: "VER OUTROS PLANOS",
        ...(isTextFlux() && { className: "secondary" }),
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("carrega_planos").index,
          });
        },
      },
    ],
  },
  {
    name: "titular_plano",
    sender: "bot",
    text: "Quem será o titular do plano?",
    options: [
      {
        text: "EU MESMO",
        action: () => {
          const userData = useUserStore.getState();
          const userContratoData = useUserStore.getState().contrato;

          useUserStore.setState({
            ...userData,
            contrato: {
              ...userContratoData,
              tipoPessoa: 0,
              tipoCliente: "Pessoa Física",
            },
          });

          useBotControlStore.setState({
            currentStep: findStep("nome_completo").index,
          });
        },
      },
      {
        text: "MINHA EMPRESA",
        action: () => {
          const userData = useUserStore.getState();
          const userContratoData = useUserStore.getState().contrato;

          useUserStore.setState({
            ...userData,
            contrato: {
              ...userContratoData,
              tipoPessoa: 1,
              tipoCliente: "Pessoa Jurídica",
            },
          });

          useBotControlStore.setState({
            currentStep: findStep("nome_completo").index,
          });
        },
      },
    ],
  },
  {
    name: "nome_completo",
    sender: "bot",
    text: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;

      return tipoPessoa === 0
        ? "Qual o seu <b>nome completo</b>?"
        : "Digite a <b>razão social</b>";
    },
    inputType: "userContratoNomeRazao",
    action: async () => {
      const { tipoPessoa } = useUserStore.getState().contrato;
      const isPessoaFisica = tipoPessoa === 0;

      const data = await noConnectionHandler().then(() => {
        return validateForbiddenWords(
          useUserStore.getState().contrato.nomeRazao,
          `Por favor, insira ${
            isPessoaFisica ? "um nome válido." : "uma razaão social válida."
          }`,
          "cpf_cnpj",
          "userContratoNomeRazao",
        );
      });

      return data;
    },
  },
  {
    name: "cpf_cnpj",
    sender: "bot",
    text: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;

      return tipoPessoa === 0
        ? "Informe o seu <b>CPF </b>"
        : "Informe o <b>CNPJ </b>";
    },
    inputType: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;
      const isAlphaNumericCNPJ = getRemoteValueEnv(
        "isAlphaNumericCNPJ_dev",
        "isAlphaNumericCNPJ_prod",
      ).asBoolean();

      if (isAlphaNumericCNPJ) {
        return tipoPessoa === 0 ? "userCpf" : "userCnpj";
      }

      return "userCpfCnpj";
    },
    action: async () => {
      const { tipoPessoa } = useUserStore.getState().contrato;
      const userCpfCnpj = useUserStore.getState().contrato.cpfCnpj;
      const hashCpfCnpj = sha256(userCpfCnpj).toString();

      tagIdentificacao(tipoPessoa, hashCpfCnpj);

      useBotControlStore.setState({
        currentStep: findStep("cep").index,
      });
    },
  },
  {
    name: "cep",
    sender: "bot",
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      return `<b>${userBasicName}</b>, qual é o seu CEP?`;
    },
    inputType: "userCep",
    action: () => {
      handleGetCep("cep-first-input");
    },
  },
  {
    name: "agradecimento_cep",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      return `Obrigado! <b>${userBasicName}</b>`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("aguarda_registro_dados").index,
      });
    },
  },
  {
    name: "aguarda_registro_dados",
    sender: "bot",
    text: "Enquanto eu registro os seus dados...",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("nascimento_fundacao").index,
      });
    },
  },
  {
    name: "nascimento_fundacao",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;

      return tipoPessoa === 0
        ? "Me informe a sua <b>data de nascimento</b>, por favor:"
        : "Me informe a sua <b>data de fundação</b> da empresa, por favor:";
    },
    inputType: "userNascimentoFundacao",
    action: async () => {
      useBotControlStore.setState({
        currentStep: findStep("revisao_dados_pessoais").index,
      });
    },
  },
  {
    name: "revisao_dados_pessoais",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      return `${userBasicName}, agora é só <b>confirmar</b> se está tudo certo:`;
    },
    action: () => {
      tagDadosContrato();

      useBotControlStore.setState({
        currentStep: findStep("confirmacao_dados_cadastrais").index,
      });
    },
  },
  {
    name: "exibe_dados_cadastrais",
    sender: "bot",
    action: () => {
      const botMessages = useBotControlStore.getState().messages;
      const botCurrentStep = useBotControlStore.getState().currentStep;
      const consorcioBem = useConsorcioStore.getState().tipoBem;
      const userTipoBemNome = ProdutosNomes[consorcioBem];
      const consorcioTipoPlano = useConsorcioStore.getState().tipoPlano;
      const selectedPlano = useConsorcioStore.getState().selectedPlano;

      const newMessage = {
        sender: "bot",
        text: `
          <b>Consórcio para ${userTipoBemNome}</b><br /><br />
          <b>Tipo:</b> ${tipoPlanoNome[consorcioTipoPlano]}<br />
          <b> Plano:</b> ${(selectedPlano.bemCredito ?? 0).toLocaleString(
            "pt-BR",
            {
              style: "currency",
              currency: "BRL",
            },
          )}<br />
          em ${selectedPlano.planoMeses} parcelas de ${(
            selectedPlano.planoValorParcela ?? 0
          ).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}<br />
          <b>${getFees(selectedPlano).text}</b> ${getFees(selectedPlano).value}
        `,
      };

      const { skipStepTracking, appendPendingMessageBlock } =
        useBotControlStore.getState();
      if (!skipStepTracking) {
        appendPendingMessageBlock({
          step: "exibe_dados_cadastrais",
          message: newMessage.text.trim(),
        });
      }

      useBotControlStore.setState({
        currentStep: botCurrentStep,
        messages: [...botMessages, newMessage],
      });
    },
    options: [
      {
        text: "CONFIRMAR",
        action: async () => {
          const hasInsurance = useInitialConfigStore.getState().seguro;

          // INSURANCE FLAG
          if (hasInsurance) {
            useBotControlStore.setState({
              currentStep: findStep("valida_seguro").index,
            });
          } else {
            shouldSelectCoupon();
          }
        },
      },
      {
        text: "DETALHES DO PLANO",
        action: handleShowPlanDetails,
      },
      {
        text: "ALTERAR DADOS",
        action: () => {
          tagAlterarDadosContrato();

          useBotControlStore.setState({
            currentStep: findStep("menu_alterar_dados").index,
          });
        },
      },
      {
        text: "FALAR COM OS ESPECIALISTAS",
        action: () => {
          const linkWhatsApp = useInitialConfigStore.getState().linkWhatsApp;
          window.open(sanitizeUrl(linkWhatsApp), "_blank");

          tagBtnWhatsapp("dados_contrato");
          acionarEspecialistaTag("dados_contrato");
        },
      },
    ],
  },
  {
    name: "mensagem_resolucao_285",
    sender: "bot",
    text: () => {
      const assemblyDate =
        useConsorcioStore.getState().selectedPlano.dataAssembleia;
      return `<b>Elegível para participação na assembleia do dia ${dayjs(
        assemblyDate,
      ).format("DD/MM/YYYY")}</b>.`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("termos_regulamento").index,
      });
    },
  },
  {
    name: "confirmacao_dados_cadastrais",
    sender: "bot",
    action: () => {
      const botMessages = useBotControlStore.getState().messages;
      const botCurrentStep = useBotControlStore.getState().currentStep;
      const { nomeRazao, cpfCnpj, nascimentoFundacao, cep } =
        useUserStore.getState().contrato;
      const { telefone, email } = useUserStore.getState().basic;

      const { tipoPessoa } = useUserStore.getState().contrato;
      const isPessoaFisica = tipoPessoa === 0;

      const nomeRazaoLabel = isPessoaFisica ? "Nome" : "Razão Social";
      const cpfCnpjLabel = isPessoaFisica ? "CPF" : "CNPJ";
      const nascimentoFundacaoLabel = isPessoaFisica
        ? "Nascimento"
        : "Fundação";

      const exibirTextoResolucao285 =
        useConsorcioStore.getState().selectedPlano.exibirTextoResolucao285;

      const newMessage = {
        sender: "bot",
        text: `
          <div class="message-ellipsis">
            <b> ${nomeRazaoLabel}:</b> ${nomeRazao}<br />
            <b> E-mail:</b> ${email}<br />
            <b> Celular:</b> ${telefone}<br />
            <b> ${cpfCnpjLabel}:</b> ${cpfCnpj}<br />
            <b> ${nascimentoFundacaoLabel}:</b> ${nascimentoFundacao}<br />
            <b> CEP:</b> ${addCepMask(cep)}
          </div>
        `,
        title: `${nomeRazao} | ${email}`,
      };

      const { skipStepTracking, appendPendingMessageBlock } =
        useBotControlStore.getState();
      if (!skipStepTracking) {
        appendPendingMessageBlock({
          step: "confirmacao_dados_cadastrais",
          message: newMessage.text.trim(),
        });
      }

      useBotControlStore.setState({
        currentStep: botCurrentStep,
        messages: [...botMessages, newMessage],
      });

      useBotControlStore.setState({
        currentStep: findStep(
          exibirTextoResolucao285
            ? "mensagem_resolucao_285"
            : "termos_regulamento",
        ).index,
      });
    },
  },
  {
    name: "termos_regulamento",
    sender: "bot",
    text: () => {
      const { empresaId, desconto, shouldUseCoupon } =
        useInitialConfigStore.getState();

      return `
              Para finalizar, preciso que você <b>leia</b> e <b>aceite</b> os termos do ${
                desconto || shouldUseCoupon
                  ? `<b>Regulamento Geral do Consórcio</b> e <b>Regulamento da Campanha.</b>`
                  : "<b>Regulamento Geral do Consórcio.</b>"
              }<br/><br/>
              <a class="link-highlight" href="https://chabot-cdn.mycon.com.br/mycon/contrato-participacao-grupo-consorcio-por-adesao.pdf" target="_blank">Regulamento Geral do Consórcio</a>
              ${
                desconto || shouldUseCoupon
                  ? `<br/><br/><a class="link-highlight" href="https://chatbot-cdn.mycon.com.br/regulamentos/consorcio-digital/${empresaId}.pdf" target="_blank">Regulamento da Campanha</a>`
                  : ""
              }
            `;
    },
    action: () => {
      termsAcceptedTag();
      useBotControlStore.setState({
        currentStep: findStep("exibe_dados_cadastrais").index,
      });
    },
  },
  {
    name: "menu_alterar_dados",
    sender: "bot",
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      return `${userBasicName}, qual informação você precisa <b>alterar</b>?`;
    },
    options: [
      {
        text: "NOME",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("alterar_nome_completo").index,
          });
        },
      },
      {
        text: "E-MAIL",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("alterar_email").index,
          });
        },
      },
      {
        text: "CPF/CNPJ",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("alterar_cpf_cnpj").index,
          });
        },
      },
      {
        text: "CELULAR",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("alterar_celular").index,
          });
        },
      },
      {
        text: "NASCIMENTO",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("alterar_nascimento").index,
          });
        },
      },
      {
        text: "CEP",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("alterar_cep").index,
          });
        },
      },
      {
        text: "NENHUMA",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("revisao_dados_pessoais").index,
          });
        },
      },
    ],
  },
  {
    name: "confirma_detalhes_plano",
    sender: "bot",
    options: () => [
      {
        text: "CONFIRMAR",
        action: () => {
          const hasInsurance = useInitialConfigStore.getState().seguro;

          // INSURANCE FLAG
          if (hasInsurance) {
            useBotControlStore.setState({
              currentStep: findStep("valida_seguro").index,
            });
          } else {
            shouldSelectCoupon();
          }
        },
      },
    ],
    action: async () => {
      // Se fetchedPlanDetails não tiver planoTaxas (ex: após recover), re-busca da API
      if (!useConsorcioStore.getState().fetchedPlanDetails?.planoTaxas) {
        try {
          const planDetails = await getPlanDetails();
          const { data } = planDetails.data;
          if (data?.planoTaxas) {
            useConsorcioStore.setState({ fetchedPlanDetails: data });
            try {
              localStorage.setItem(
                "mycon_fetched_plan_details",
                JSON.stringify(data),
              );
            } catch (_) {}
          }
        } catch (_) {}
      }

      const botMessages = useBotControlStore.getState().messages;
      const planInfosMessage = {
        id: "plan-infos",
        sender: "bot",
        text: GroupInfoOptions(),
      };

      const { skipStepTracking } = useBotControlStore.getState();
      if (!skipStepTracking) {
        trackConversacaoStep({
          stepName: "confirma_detalhes_plano",
          botMessage: planInfosMessage.text,
        });
      }

      useBotControlStore.setState({
        messages: [...botMessages, planInfosMessage],
      });
    },
  },
  {
    name: "confirma_plano_selecionado",
    sender: "bot",
    action: () => {
      const botMessages = useBotControlStore.getState().messages;
      const planInfosMessage = {
        id: "plan-infos",
        sender: "bot",
        text: GroupInfoOptions(),
      };

      useBotControlStore.setState({
        messages: [...botMessages, planInfosMessage],
      });
    },
    options: () => [
      {
        text: "SEGUIR EM FRENTE",
        action: async () => {
          useBotControlStore.setState({
            currentStep: findStep("titular_plano").index,
          });
        },
      },
      {
        text: "VER OUTROS PLANOS",
        ...(isTextFlux() && { className: "secondary" }),
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("carrega_planos").index,
          });
        },
      },
    ],
  },
  {
    name: "alterar_nome_completo",
    sender: "bot",
    text: "Altere o <b>nome completo</b> que vai no contrato.",
    inputType: "userContratoNomeRazao",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("revisao_dados_pessoais").index,
      });
    },
  },
  {
    name: "aguarda_proposta",
    sender: "bot",
    text: "<b>Aguarde</b> um momento...",
    isWaitingForFetch: isInsuranceHired,
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("proposta_criada").index,
      });
    },
  },
  {
    name: "proposta_criada",
    sender: "bot",
    text: () => {
      const contrato = useConsorcioStore.getState().fetchedProposta.contrato;
      return `Pronto! Sua proposta nº <b>${contrato} foi efetivada com sucesso!</b>`;
    },
    action: async () => {
      const proposta = useConsorcioStore.getState().fetchedProposta;

      const validMethods =
        proposta.formasPagamentoDisponiveis?.filter((code) =>
          [5, 0, 1].includes(code),
        ) || [];

      if (validMethods.length === 0) {
        useBotControlStore.setState({
          currentStep: findStep("sem_metodos_pagamento").index,
        });
      } else {
        useBotControlStore.setState({
          currentStep: findStep("inicio_pagamento").index,
        });
      }
    },
  },
  {
    name: "comprar_outra_cota",
    sender: "bot",
    text: "Você quer <b>comprar outra</b> cota?",
    options: [
      {
        text: "SIM",
        action: async () => {
          await startNewConversacaoSession();
          useBotControlStore.setState({
            currentStep: findStep("pergunta_mesmo_credito").index,
          });
        },
      },
      {
        text: "NÃO",
        action: () => {
          useBotControlStore.setState({
            currentStep: findStep("inicio_pesquisa").index,
          });
        },
      },
    ],
  },
  {
    name: "alterar_email",
    sender: "bot",
    text: "Altere seu <b>e-mail</b>",
    inputType: "userEmail",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("revisao_dados_pessoais").index,
      });
    },
  },
  {
    name: "alterar_cpf_cnpj",
    sender: "bot",
    text: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;

      return tipoPessoa === 0
        ? "Altere o seu <b>CPF </b>"
        : "Altere o <b>CNPJ </b>";
    },
    inputType: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;

      return tipoPessoa === 0 ? "userCpf" : "userCnpj";
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("revisao_dados_pessoais").index,
      });
    },
  },
  {
    name: "alterar_celular",
    sender: "bot",
    text: "Altere seu <b>celular</b> (com DDD)",
    inputType: "userTelefone",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("revisao_dados_pessoais").index,
      });
    },
  },
  {
    name: "alterar_nascimento",
    sender: "bot",
    text: () => {
      const { tipoPessoa } = useUserStore.getState().contrato;

      return tipoPessoa === 0
        ? "Altere a <b>data de nascimento</b>:"
        : "Altere a <b>data de fundação</b>:";
    },
    inputType: "userNascimentoFundacao",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("revisao_dados_pessoais").index,
      });
    },
  },
  {
    name: "alterar_cep",
    sender: "bot",
    text: "Altere CEP:",
    inputType: "userCep",
    action: () => {
      handleGetCep("update");
    },
  },
];

export default proposalSteps;
